import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Clock,
  Users,
  Settings,
  Save,
  Plus,
  Edit3,
  Trash2,
  X,
  CreditCard,
  Mail,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  DollarSign,
  FileText,
  Package,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';

const ManageAppointmentCalendar = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Service Providers
  const [serviceProviders, setServiceProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [providerSchedules, setProviderSchedules] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  
  // Custom Fields
  const [customFields, setCustomFields] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  
  // Services
  const [servicesEnabled, setServicesEnabled] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingService, setEditingService] = useState(null);
  
  // Payment Settings
  const [paymentSettings, setPaymentSettings] = useState({
    payment_enabled: false,
    token_amount: 0,
    selected_payment_gateway: null
  });
  const [availableGateways, setAvailableGateways] = useState([]);
  
  // Communication Settings
  const [communicationSettings, setCommunicationSettings] = useState({
    email_enabled: false,
    sms_enabled: false
  });

  // Multiple Bookings Settings
  const [multipleBookingsEnabled, setMultipleBookingsEnabled] = useState(false);
  const [selectedProvidersForMultiple, setSelectedProvidersForMultiple] = useState([]);
  
  // Appointment Number Settings
  const [appointmentNumberPrefix, setAppointmentNumberPrefix] = useState('APPT');
  const [appointmentNumberStart, setAppointmentNumberStart] = useState(1000);
  const [showResetCounterModal, setShowResetCounterModal] = useState(false);

  // Appointment Slot Settings
  const [appointmentSlotConfig, setAppointmentSlotConfig] = useState({
    slot_interval_minutes: 30,
    first_appointment_delay_minutes: 0,
    last_appointment_cutoff_minutes: 30
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to format field type for display
  const formatFieldType = (type) => {
    const typeMap = {
      'text': 'Text',
      'email': 'Email',
      'phone': 'Phone Number',
      'date': 'Date',
      'number': 'Number',
      'textarea': 'Text Area',
      'dropdown': 'Dropdown'
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  const primaryColor = 'var(--theme-primary)';

  const tabs = [
    { id: 0, title: 'Manage Schedules', icon: Calendar },
    { id: 1, title: 'Configure Form', icon: Users },
    { id: 2, title: 'Configure Services', icon: Package },
    { id: 3, title: 'Payment Settings', icon: CreditCard },
    { id: 4, title: 'Communication', icon: MessageSquare },
    { id: 5, title: 'Settings', icon: Settings }
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const getScheduleForDay = (providerId, dayOfWeek) => {
    if (!providerSchedules || !selectedProviderId) return null;
    return providerSchedules.find(s => s.service_provider_id === providerId && s.day_of_week === dayOfWeek);
  };

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  // Initialize week to current Monday
  useEffect(() => {
    const today = new Date();
    const monday = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    monday.setDate(today.getDate() + daysToMonday);
    setCurrentWeekStart(monday);
  }, []);

  const durationOptions = [];
  for (let i = 15; i <= 120; i += 15) {
    durationOptions.push({ value: i, label: `${i} minutes` });
  }

  useEffect(() => {
    if (token) {
      fetchServiceProviders();
      fetchCustomFields();
      fetchServicesToggle();
      fetchServiceCategories();
      fetchServices();
      fetchPaymentSettings();
      fetchAvailableGateways();
      fetchCommunicationSettings();
      fetchBookingSettings();
      fetchAppointmentSlotConfig();
    }
  }, [token]);

  useEffect(() => {
    if (serviceProviders.length > 0) {
      fetchAllProviderSchedules();
    }
  }, [serviceProviders]);

  const fetchServiceProviders = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/service-providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServiceProviders(response.data.service_providers);
        if (response.data.service_providers.length > 0) {
          setSelectedProviderId(response.data.service_providers[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching service providers:', error);
    }
  };

  const fetchProviderSchedules = async (providerId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/service-provider-schedules/${providerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        return response.data.schedules;
      }
      return [];
    } catch (error) {
      console.error('Error fetching schedules:', error);
      return [];
    }
  };

  const fetchAllProviderSchedules = async () => {
    try {
      const allSchedules = [];
      for (const provider of serviceProviders) {
        const schedules = await fetchProviderSchedules(provider.id);
        allSchedules.push(...schedules);
      }
      setProviderSchedules(allSchedules);
    } catch (error) {
      console.error('Error fetching all schedules:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-custom-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomFields(response.data.fields);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const moveFieldUp = async (fieldId, currentIndex) => {
    if (currentIndex === 0) return; // Already at the top
    
    try {
      const newFields = [...customFields];
      const temp = newFields[currentIndex];
      newFields[currentIndex] = newFields[currentIndex - 1];
      newFields[currentIndex - 1] = temp;
      
      // Update order values
      newFields.forEach((field, index) => {
        field.order = index;
      });
      
      // Update UI immediately
      setCustomFields(newFields);
      
      // Update backend
      await axios.put(
        `${API_BASE_URL}/api/appointment-custom-fields/${fieldId}`,
        { order: currentIndex - 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await axios.put(
        `${API_BASE_URL}/api/appointment-custom-fields/${newFields[currentIndex].id}`,
        { order: currentIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showMessage('success', 'Field order updated');
    } catch (error) {
      console.error('Error updating field order:', error);
      showMessage('error', 'Failed to update field order');
      fetchCustomFields(); // Revert on error
    }
  };

  const moveFieldDown = async (fieldId, currentIndex) => {
    if (currentIndex === customFields.length - 1) return; // Already at the bottom
    
    try {
      const newFields = [...customFields];
      const temp = newFields[currentIndex];
      newFields[currentIndex] = newFields[currentIndex + 1];
      newFields[currentIndex + 1] = temp;
      
      // Update order values
      newFields.forEach((field, index) => {
        field.order = index;
      });
      
      // Update UI immediately
      setCustomFields(newFields);
      
      // Update backend
      await axios.put(
        `${API_BASE_URL}/api/appointment-custom-fields/${fieldId}`,
        { order: currentIndex + 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await axios.put(
        `${API_BASE_URL}/api/appointment-custom-fields/${newFields[currentIndex].id}`,
        { order: currentIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showMessage('success', 'Field order updated');
    } catch (error) {
      console.error('Error updating field order:', error);
      showMessage('error', 'Failed to update field order');
      fetchCustomFields(); // Revert on error
    }
  };

  const handleDeleteField = async () => {
    if (!fieldToDelete) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/appointment-custom-fields/${fieldToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCustomFields();
      showMessage('success', 'Field deleted successfully');
      setShowDeleteFieldModal(false);
      setFieldToDelete(null);
    } catch (error) {
      console.error('Error deleting field:', error);
      showMessage('error', 'Failed to delete field');
    }
  };

  const fetchServicesToggle = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-services-toggle`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServicesEnabled(response.data.settings.services_enabled);
      }
    } catch (error) {
      console.error('Error fetching services toggle:', error);
    }
  };

  const fetchServiceCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-service-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServiceCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching service categories:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServices(response.data.services);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-payment-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPaymentSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const fetchAvailableGateways = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const enabled = response.data.gateways.filter(g => g.enabled && g.is_verified);
        setAvailableGateways(enabled);
      }
    } catch (error) {
      console.error('Error fetching available gateways:', error);
    }
  };

  const fetchCommunicationSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-communication-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCommunicationSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching communication settings:', error);
    }
  };

  const fetchBookingSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-booking-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setMultipleBookingsEnabled(response.data.settings.multiple_bookings_enabled || false);
        setSelectedProvidersForMultiple(response.data.settings.providers_for_multiple || []);
      }
      
      // Fetch appointment number settings
      const numberResponse = await axios.get(`${API_BASE_URL}/api/appointment-number-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (numberResponse.data.success) {
        setAppointmentNumberPrefix(numberResponse.data.settings.prefix || 'APPT');
        setAppointmentNumberStart(numberResponse.data.settings.starting_number || 1000);
      }
    } catch (error) {
      console.error('Error fetching booking settings:', error);
    }
  };

  const fetchAppointmentSlotConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-slot-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setAppointmentSlotConfig({
          slot_interval_minutes: response.data.slot_interval_minutes,
          first_appointment_delay_minutes: response.data.first_appointment_delay_minutes,
          last_appointment_cutoff_minutes: response.data.last_appointment_cutoff_minutes
        });
      }
    } catch (error) {
      console.error('Error fetching appointment slot config:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleSavePaymentSettings = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/appointment-payment-settings`,
        paymentSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showMessage('success', 'Payment settings saved successfully');
      }
    } catch (error) {
      showMessage('error', 'Failed to save payment settings');
    }
  };

  const handleSaveCommunicationSettings = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/appointment-communication-settings`,
        communicationSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showMessage('success', 'Communication settings saved successfully');
      }
    } catch (error) {
      showMessage('error', 'Failed to save communication settings');
    }
  };

  const handleSaveBookingSettings = async () => {
    try {
      // Save multiple bookings settings
      const response = await axios.put(
        `${API_BASE_URL}/api/appointment-booking-settings`,
        {
          multiple_bookings_enabled: multipleBookingsEnabled,
          providers_for_multiple: selectedProvidersForMultiple
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Save appointment number settings
      const numberResponse = await axios.put(
        `${API_BASE_URL}/api/appointment-number-settings`,
        {
          prefix: appointmentNumberPrefix,
          starting_number: appointmentNumberStart
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && numberResponse.data.success) {
        showMessage('success', 'Booking settings saved successfully');
      }
    } catch (error) {
      showMessage('error', 'Failed to save booking settings');
    }
  };

  const handleSaveAppointmentSlotConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/appointment-slot-config`,
        appointmentSlotConfig,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showMessage('success', 'Appointment slot configuration saved successfully');
      }
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to save appointment slot configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotConfigChange = (field, value) => {
    setAppointmentSlotConfig({
      ...appointmentSlotConfig,
      [field]: parseInt(value)
    });
  };

  const handleResetCounter = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/appointment-number-settings/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showMessage('success', 'Appointment counter reset successfully');
        setShowResetCounterModal(false);
        // Refresh settings
        fetchBookingSettings();
      }
    } catch (error) {
      showMessage('error', 'Failed to reset counter');
    }
  };

  const handleToggleServices = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/appointment-services-toggle`,
        { services_enabled: !servicesEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setServicesEnabled(!servicesEnabled);
        showMessage('success', `Services ${!servicesEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      showMessage('error', 'Failed to toggle services');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-8 py-4">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-lg mb-6 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6" style={{ color: primaryColor }} />
            <h2 className="text-lg font-semibold text-gray-900">
              Manage Appointment Calendar
            </h2>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          {message.text}
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-white shadow-sm p-6">
        {activeTab === 0 && (
          <div>
            {serviceProviders.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">No service providers found</p>
                <p className="text-sm text-gray-500">
                  Add employees with "Service Provider" role in Employee Management
                </p>
              </div>
            ) : (
              <div>
                {/* Week Navigation */}
                <div className="mb-4 flex items-center justify-between">
                  <button
                    onClick={() => navigateWeek(-1)}
                    className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous Week</span>
                  </button>
                  
                  <div className="text-center">
                    <h3 className="text-sm font-medium text-gray-900">
                      Week of {formatDateForDisplay(currentWeekStart)}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {formatDateForAPI(currentWeekStart)} to {formatDateForAPI(getWeekDates()[6])}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => navigateWeek(1)}
                    className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                  >
                    <span>Next Week</span>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <div className="min-w-full" style={{ minWidth: '800px' }}>
                    {/* Week Header */}
                    <div className="grid grid-cols-8 bg-gray-50 border-b border-gray-200">
                      <div className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Provider
                      </div>
                      {getWeekDates().map((date, index) => (
                        <div key={index} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div>{formatDateForDisplay(date)}</div>
                          <div className="text-gray-400 font-normal text-xs">
                            {formatDateForAPI(date)}
                          </div>
                        </div>
                      ))}
                    </div>

                  {/* Schedule Rows */}
                  <div className="bg-white divide-y divide-gray-200">
                    {serviceProviders.map((provider) => (
                      <div key={provider.id} className="grid grid-cols-8">
                        {/* Provider Info */}
                        <div className="px-4 py-6 border-r border-gray-200">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {provider.full_name}
                            </div>
                          </div>
                        </div>

                        {/* Daily Schedule Cells */}
                        {getWeekDates().map((date, dateIndex) => {
                          const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                          const schedule = getScheduleForDay(provider.id, dayOfWeek);
                          
                          return (
                            <div key={dateIndex} className="px-2 py-4 border-r border-gray-200 min-h-[100px]">
                              {schedule ? (
                                <div className={`rounded-lg border-2 border-dashed p-3 ${schedule.is_enabled ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'}`}>
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="text-xs font-medium text-gray-900">
                                      {schedule.start_time} - {schedule.end_time}
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-600 mb-2">
                                    {schedule.appointment_duration_minutes} min slots
                                  </div>
                                  {schedule.breaks && schedule.breaks.length > 0 && (
                                    <div className="text-xs text-gray-500 mb-2">
                                      {schedule.breaks.length} break{schedule.breaks.length > 1 ? 's' : ''}
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between mt-2">
                                    <label className="flex items-center gap-1">
                                      <input
                                        type="checkbox"
                                        checked={schedule.is_enabled}
                                        onChange={async () => {
                                          try {
                                            await axios.put(
                                              `${API_BASE_URL}/api/service-provider-schedules/${schedule.id}`,
                                              { is_enabled: !schedule.is_enabled },
                                              { headers: { Authorization: `Bearer ${token}` } }
                                            );
                                            fetchAllProviderSchedules();
                                            showMessage('success', 'Schedule updated');
                                          } catch (error) {
                                            showMessage('error', 'Failed to update schedule');
                                          }
                                        }}
                                        className="rounded text-xs"
                                      />
                                      <span className="text-xs text-gray-600">On</span>
                                    </label>
                                    <button
                                      onClick={() => {
                                        setSelectedProviderId(provider.id);
                                        setEditingSchedule(schedule);
                                        setShowScheduleModal(true);
                                      }}
                                      className="text-theme-blue hover:text-blue-700"
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setSelectedProviderId(provider.id);
                                    setEditingSchedule({ day_of_week: dayOfWeek });
                                    setShowScheduleModal(true);
                                  }}
                                  className="w-full h-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 transition-colors"
                                >
                                  <Plus className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 1 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Configure Form</h2>
              </div>
              <button
                onClick={() => {
                  setEditingField({
                    field_name: '',
                    field_type: 'text',
                    is_required: true,
                    dropdown_options: []
                  });
                  setShowFieldModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                <Plus className="h-4 w-4" />
                Add Field
              </button>
            </div>

            <div className="space-y-3">
              {customFields.map((field, index) => (
                <div key={field.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3 flex-1">
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveFieldUp(field.id, index)}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-[var(--theme-primary)] rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => moveFieldDown(field.id, index)}
                        disabled={index === customFields.length - 1}
                        className="p-1 text-gray-400 hover:text-[var(--theme-primary)] rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {/* Field info */}
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{field.field_name}</div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        Type: {formatFieldType(field.field_type)} | {field.is_required ? 'Required' : 'Optional'}
                        {field.is_system_field && <span className="ml-2 text-xs text-theme-blue">(System Field)</span>}
                      </div>
                    </div>
                  </div>
                  
                  {/* Edit and Delete buttons */}
                  {!field.is_system_field && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setEditingField(field);
                          setShowFieldModal(true);
                        }}
                        className="p-2 text-gray-600 hover:text-[var(--theme-primary)] rounded-lg transition-colors"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setFieldToDelete(field);
                          setShowDeleteFieldModal(true);
                        }}
                        className="p-2 text-red-600 hover:text-red-700 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {customFields.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No custom fields added yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 2 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Configure Services</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Enable this to show service selection in the booking form.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Show Services in Booking</span>
                <button
                  onClick={handleToggleServices}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    servicesEnabled ? 'bg-[var(--theme-primary)]' : 'bg-gray-200'
                  }`}
                  style={servicesEnabled ? { backgroundColor: primaryColor } : {}}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      servicesEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {servicesEnabled && (
              <div className="mt-4">
                <button
                  onClick={() => navigate('/service-management')}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Package className="h-4 w-4" />
                  Go to Service Management
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 3 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Payment Settings</h2>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Payment Requirement</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Require customers to pay a token amount when booking
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentSettings.payment_enabled}
                    onChange={(e) => setPaymentSettings({ ...paymentSettings, payment_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div 
                    className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                    style={paymentSettings.payment_enabled ? { 
                      backgroundColor: 'var(--theme-primary)',
                      boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                    } : {}}
                  ></div>
                </label>
              </div>

              {paymentSettings.payment_enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Token Amount ($)
                    </label>
                    <input
                      type="number"
                      value={paymentSettings.token_amount}
                      onChange={(e) => setPaymentSettings({ ...paymentSettings, token_amount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Gateway
                    </label>
                    <select
                      value={paymentSettings.selected_payment_gateway || ''}
                      onChange={(e) => setPaymentSettings({ ...paymentSettings, selected_payment_gateway: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    >
                      <option value="">Select Payment Gateway</option>
                      {availableGateways.map((gateway) => (
                        <option key={gateway.gateway} value={gateway.gateway}>
                          {gateway.gateway.charAt(0).toUpperCase() + gateway.gateway.slice(1)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Only enabled and verified gateways are shown. Configure in Payment Settings.
                    </p>
                  </div>
                </>
              )}

              <button
                onClick={handleSavePaymentSettings}
                className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="h-4 w-4" />
                Save Payment Settings
              </button>
            </div>
          </div>
        )}

        {activeTab === 4 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Communication Settings</h2>
            
            <div className="space-y-4">
              {/* Email Notifications Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Email Notifications</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Send email confirmation when customers book appointments
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={communicationSettings.email_enabled}
                    onChange={(e) => setCommunicationSettings({ ...communicationSettings, email_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div 
                    className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                    style={communicationSettings.email_enabled ? { 
                      backgroundColor: 'var(--theme-primary)',
                      boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                    } : {}}
                  ></div>
                </label>
              </div>

              {/* SMS Notifications Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable SMS Notifications</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Send SMS confirmation when customers book appointments
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={communicationSettings.sms_enabled}
                    onChange={(e) => setCommunicationSettings({ ...communicationSettings, sms_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div 
                    className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                    style={communicationSettings.sms_enabled ? { 
                      backgroundColor: 'var(--theme-primary)',
                      boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                    } : {}}
                  ></div>
                </label>
              </div>

              <button
                onClick={handleSaveCommunicationSettings}
                className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="h-4 w-4" />
                Save Communication Settings
              </button>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 5 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Booking Settings</h2>
            
            <div className="space-y-6">
              {/* Enable Multiple Bookings Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Multiple Bookings Per Slot</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Allow multiple customers to book the same time slot with selected providers
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={multipleBookingsEnabled}
                    onChange={(e) => setMultipleBookingsEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div 
                    className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                    style={multipleBookingsEnabled ? { 
                      backgroundColor: 'var(--theme-primary)',
                      boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                    } : {}}
                  ></div>
                </label>
              </div>

              {/* Provider Selection List */}
              {multipleBookingsEnabled && (
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Select Providers for Multiple Bookings</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Choose which providers can accept multiple appointments for the same time slot
                  </p>
                  
                  {serviceProviders.length === 0 ? (
                    <div className="text-center py-6">
                      <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No providers available</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {serviceProviders.map((provider) => (
                        <div
                          key={provider.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="text-sm font-medium text-gray-900">{provider.full_name}</span>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedProvidersForMultiple.includes(provider.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProvidersForMultiple([...selectedProvidersForMultiple, provider.id]);
                                } else {
                                  setSelectedProvidersForMultiple(selectedProvidersForMultiple.filter(id => id !== provider.id));
                                }
                              }}
                              className="sr-only peer"
                            />
                            <div 
                              className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                              style={selectedProvidersForMultiple.includes(provider.id) ? { 
                                backgroundColor: 'var(--theme-primary)',
                                boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                              } : {}}
                            ></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Appointment Number Settings */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Appointment Number Configuration</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Configure the format for appointment confirmation numbers
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prefix
                    </label>
                    <input
                      type="text"
                      value={appointmentNumberPrefix}
                      onChange={(e) => setAppointmentNumberPrefix(e.target.value.toUpperCase())}
                      placeholder="APPT"
                      maxLength={10}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">e.g., APPT, APM, BOOK</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Starting Number
                    </label>
                    <input
                      type="number"
                      value={appointmentNumberStart}
                      onChange={(e) => setAppointmentNumberStart(parseInt(e.target.value) || 1000)}
                      min="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">e.g., 1000, 5000, 10000</p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4 text-theme-blue" />
                    <span className="text-xs font-medium text-blue-900">Preview</span>
                  </div>
                  <div className="text-sm font-bold text-blue-700">
                    {appointmentNumberPrefix || 'APPT'}{appointmentNumberStart || 1000}
                  </div>
                  <p className="text-xs text-theme-blue mt-1">
                    Next appointment will be: {appointmentNumberPrefix || 'APPT'}{(appointmentNumberStart || 1000) + 1}
                  </p>
                </div>
                
                <p className="text-xs text-yellow-600 mt-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Warning: Changing the starting number will reset the counter. This cannot be undone.</span>
                </p>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowResetCounterModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reset Counter to Starting Number
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    This will reset the counter back to {appointmentNumberStart || 1000}, making the next appointment number {appointmentNumberPrefix || 'APPT'}{appointmentNumberStart || 1000}
                  </p>
                </div>
              </div>

              {/* Appointment Slot Configuration */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Appointment Slot Configuration</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Configure how appointment time slots are generated and displayed to customers
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Slot Interval */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slot Interval
                    </label>
                    <select
                      value={appointmentSlotConfig.slot_interval_minutes}
                      onChange={(e) => handleSlotConfigChange('slot_interval_minutes', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>1 hour</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Time between appointment slots
                    </p>
                  </div>

                  {/* First Appointment Delay */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Appointment Delay
                    </label>
                    <select
                      value={appointmentSlotConfig.first_appointment_delay_minutes}
                      onChange={(e) => handleSlotConfigChange('first_appointment_delay_minutes', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    >
                      <option value={0}>Immediately at opening</option>
                      <option value={15}>15 minutes after opening</option>
                      <option value={30}>30 minutes after opening</option>
                      <option value={45}>45 minutes after opening</option>
                      <option value={60}>1 hour after opening</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      When first appointment can be scheduled
                    </p>
                  </div>

                  {/* Last Appointment Cutoff */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Appointment Cutoff
                    </label>
                    <select
                      value={appointmentSlotConfig.last_appointment_cutoff_minutes}
                      onChange={(e) => handleSlotConfigChange('last_appointment_cutoff_minutes', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    >
                      <option value={15}>15 minutes before closing</option>
                      <option value={30}>30 minutes before closing</option>
                      <option value={45}>45 minutes before closing</option>
                      <option value={60}>1 hour before closing</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      How early to stop accepting appointments
                    </p>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-blue-900">Configuration Preview</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="font-medium text-gray-700">Slot Duration:</span>
                      <div className="text-blue-700 font-semibold">
                        {appointmentSlotConfig.slot_interval_minutes} minutes
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">First Slot:</span>
                      <div className="text-blue-700 font-semibold">
                        {appointmentSlotConfig.first_appointment_delay_minutes === 0 
                          ? 'At opening time' 
                          : `${appointmentSlotConfig.first_appointment_delay_minutes} min after opening`}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Last Slot:</span>
                      <div className="text-blue-700 font-semibold">
                        {appointmentSlotConfig.last_appointment_cutoff_minutes} min before closing
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleSaveAppointmentSlotConfig}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = 'var(--theme-primary-hover)')}
                    onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = 'var(--theme-primary)')}
                  >
                    <Save className="h-4 w-4" />
                    {loading ? 'Saving...' : 'Save Slot Configuration'}
                  </button>
                </div>
              </div>

              {/* Delete All Appointments Section */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="text-sm font-medium text-red-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Danger Zone
                </h3>
                <p className="text-xs text-red-700 mb-4">
                  Permanently delete all appointment bookings from the system. This action cannot be undone.
                </p>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete ALL appointments? This action cannot be undone!')) {
                      // Make API call to delete all appointments
                      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
                      const token = localStorage.getItem('token');
                      fetch(`${API_BASE_URL}/api/appointment-bookings/delete-all`, {
                        method: 'DELETE',
                        headers: { 
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      })
                      .then(res => res.json())
                      .then(data => {
                        if (data.success) {
                          alert(`Successfully deleted ${data.deleted_count} appointments`);
                        }
                      })
                      .catch(err => {
                        console.error('Error:', err);
                        alert('Failed to delete appointments');
                      });
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete All Appointments
                </button>
              </div>

              <button
                onClick={handleSaveBookingSettings}
                className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="h-4 w-4" />
                Save Booking Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-lg font-semibold text-white">
                {editingSchedule?.id ? 'Edit' : 'Add'} Schedule - {daysOfWeek.find(d => d.value === editingSchedule?.day_of_week)?.label}
              </h3>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={editingSchedule?.start_time || '09:00'}
                      onChange={(e) => setEditingSchedule({ ...editingSchedule, start_time: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={editingSchedule?.end_time || '17:00'}
                      onChange={(e) => setEditingSchedule({ ...editingSchedule, end_time: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Appointment Duration
                  </label>
                  <select
                    value={editingSchedule?.appointment_duration_minutes || 30}
                    onChange={(e) => setEditingSchedule({ ...editingSchedule, appointment_duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                  >
                    {durationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Breaks Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Breaks
                    </label>
                    <button
                      onClick={() => {
                        const breaks = editingSchedule?.breaks || [];
                        setEditingSchedule({
                          ...editingSchedule,
                          breaks: [...breaks, { start_time: '12:00', end_time: '13:00', break_name: 'Lunch Break' }]
                        });
                      }}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-white rounded-lg transition-colors"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Plus className="h-3 w-3" />
                      Add Break
                    </button>
                  </div>

                  {(!editingSchedule?.breaks || editingSchedule.breaks.length === 0) ? (
                    <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                      No breaks added. Click "Add Break" to add one.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {editingSchedule.breaks.map((breakItem, index) => (
                        <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Start Time
                              </label>
                              <input
                                type="time"
                                value={breakItem.start_time}
                                onChange={(e) => {
                                  const newBreaks = [...editingSchedule.breaks];
                                  newBreaks[index].start_time = e.target.value;
                                  setEditingSchedule({ ...editingSchedule, breaks: newBreaks });
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                End Time
                              </label>
                              <input
                                type="time"
                                value={breakItem.end_time}
                                onChange={(e) => {
                                  const newBreaks = [...editingSchedule.breaks];
                                  newBreaks[index].end_time = e.target.value;
                                  setEditingSchedule({ ...editingSchedule, breaks: newBreaks });
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Name
                              </label>
                              <input
                                type="text"
                                value={breakItem.break_name || ''}
                                onChange={(e) => {
                                  const newBreaks = [...editingSchedule.breaks];
                                  newBreaks[index].break_name = e.target.value;
                                  setEditingSchedule({ ...editingSchedule, breaks: newBreaks });
                                }}
                                placeholder="Break name"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const newBreaks = editingSchedule.breaks.filter((_, i) => i !== index);
                              setEditingSchedule({ ...editingSchedule, breaks: newBreaks });
                            }}
                            className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 transition-colors"
                          >
                            <Trash2 className="h-3 w-3" />
                            Remove Break
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingSchedule?.is_enabled !== false}
                      onChange={(e) => setEditingSchedule({ ...editingSchedule, is_enabled: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Schedule Enabled</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    if (editingSchedule?.id) {
                      // Update existing
                      await axios.put(
                        `${API_BASE_URL}/api/service-provider-schedules/${editingSchedule.id}`,
                        {
                          start_time: editingSchedule.start_time,
                          end_time: editingSchedule.end_time,
                          appointment_duration_minutes: editingSchedule.appointment_duration_minutes,
                          is_enabled: editingSchedule.is_enabled,
                          breaks: editingSchedule.breaks || []
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      showMessage('success', 'Schedule updated successfully');
                    } else {
                      // Create new
                      await axios.post(
                        `${API_BASE_URL}/api/service-provider-schedules`,
                        {
                          service_provider_id: selectedProviderId,
                          day_of_week: editingSchedule.day_of_week,
                          start_time: editingSchedule.start_time || '09:00',
                          end_time: editingSchedule.end_time || '17:00',
                          appointment_duration_minutes: editingSchedule.appointment_duration_minutes || 30,
                          is_enabled: editingSchedule.is_enabled !== false,
                          breaks: []
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      showMessage('success', 'Schedule created successfully');
                    }
                    fetchAllProviderSchedules();
                    setShowScheduleModal(false);
                  } catch (error) {
                    showMessage('error', error.response?.data?.detail || 'Failed to save schedule');
                  }
                }}
                className="px-6 py-2 text-white rounded-lg font-medium transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                Save Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Field Modal */}
      {showFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: primaryColor }}>
              <h3 className="text-base font-semibold text-white">
                {editingField ? 'Edit' : 'Add'} Custom Field
              </h3>
              <button
                onClick={() => {
                  setShowFieldModal(false);
                  setEditingField(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-4">
                {editingField?.is_system_field && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>System Field:</strong> This is a default field required for customer identification. You cannot modify the field name or type.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingField?.field_name || ''}
                    onChange={(e) => setEditingField({ ...editingField, field_name: e.target.value })}
                    disabled={editingField?.is_system_field}
                    placeholder="e.g., Email Address"
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editingField?.field_type || 'text'}
                    onChange={(e) => setEditingField({ ...editingField, field_type: e.target.value })}
                    disabled={editingField?.is_system_field}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="text">Text Input</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone Number</option>
                    <option value="date">Date</option>
                    <option value="dropdown">Dropdown</option>
                  </select>
                </div>

                {editingField?.field_type === 'dropdown' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Dropdown Options <span className="text-red-500">*</span>
                      </label>
                      <button
                        onClick={() => {
                          const options = editingField?.dropdown_options || [];
                          setEditingField({
                            ...editingField,
                            dropdown_options: [...options, '']
                          });
                        }}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-white rounded-lg transition-colors"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Plus className="h-3 w-3" />
                        Add Item
                      </button>
                    </div>

                    {(!editingField?.dropdown_options || editingField.dropdown_options.length === 0) ? (
                      <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                        No options added. Click "Add Item" to add one.
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {editingField.dropdown_options.map((option, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...editingField.dropdown_options];
                                newOptions[index] = e.target.value;
                                setEditingField({ ...editingField, dropdown_options: newOptions });
                              }}
                              placeholder={`Option ${index + 1}`}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                            />
                            <button
                              onClick={() => {
                                const newOptions = editingField.dropdown_options.filter((_, i) => i !== index);
                                setEditingField({ ...editingField, dropdown_options: newOptions });
                              }}
                              className="p-2 text-red-600 hover:text-red-700 rounded-lg transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingField?.is_required !== false}
                      onChange={(e) => setEditingField({ ...editingField, is_required: e.target.checked })}
                      disabled={editingField?.is_system_field}
                      className="rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Required Field
                      {editingField?.is_system_field && (
                        <span className="ml-2 text-xs text-theme-blue">(System Field - Cannot be changed)</span>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0 bg-white">
              <button
                onClick={() => {
                  setShowFieldModal(false);
                  setEditingField(null);
                }}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    if (!editingField?.field_name || !editingField?.field_type) {
                      showMessage('error', 'Please fill in all required fields');
                      return;
                    }

                    // Filter out empty options
                    const filteredOptions = editingField?.dropdown_options?.filter(opt => opt.trim() !== '') || [];
                    
                    if (editingField?.field_type === 'dropdown' && filteredOptions.length === 0) {
                      showMessage('error', 'Please add at least one dropdown option');
                      return;
                    }

                    if (editingField?.id) {
                      // Update existing
                      await axios.put(
                        `${API_BASE_URL}/api/appointment-custom-fields/${editingField.id}`,
                        {
                          field_name: editingField.field_name,
                          is_required: editingField.is_required,
                          dropdown_options: filteredOptions
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      showMessage('success', 'Field updated successfully');
                    } else {
                      // Auto-generate field key from field name + timestamp
                      let baseKey = editingField.field_name
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^_+|_+$/g, '');
                      
                      // Fallback if baseKey is empty (all special characters)
                      if (!baseKey || baseKey === '') {
                        baseKey = 'custom_field';
                      }
                      
                      // Use timestamp + random number for better uniqueness
                      const timestamp = Date.now();
                      const random = Math.floor(Math.random() * 1000);
                      const fieldKey = `${baseKey}_${timestamp}_${random}`;
                      
                      console.log('Creating field with data:', {
                        field_name: editingField.field_name,
                        field_key: fieldKey,
                        field_type: editingField.field_type,
                        is_required: editingField.is_required !== false,
                        dropdown_options: filteredOptions,
                        order: customFields.length
                      }); // Debug
                      
                      // Create new
                      await axios.post(
                        `${API_BASE_URL}/api/appointment-custom-fields`,
                        {
                          field_name: editingField.field_name,
                          field_key: fieldKey,
                          field_type: editingField.field_type,
                          is_required: editingField.is_required !== false,
                          dropdown_options: filteredOptions,
                          order: customFields.length
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      showMessage('success', 'Field created successfully');
                    }
                    fetchCustomFields();
                    setShowFieldModal(false);
                    setEditingField(null);
                  } catch (error) {
                    console.error('Error saving field:', error.response?.data);
                    const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to save field';
                    showMessage('error', errorMessage);
                  }
                }}
                className="px-4 py-2 text-sm text-white rounded-lg font-medium transition-colors"
                style={{ backgroundColor: primaryColor }}
              >
                {editingField?.id ? 'Update' : 'Create'} Field
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Field Confirmation Modal */}
      {showDeleteFieldModal && fieldToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Custom Field
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                Are you sure you want to delete the field <span className="font-semibold text-gray-900">"{fieldToDelete.field_name}"</span>? This action cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowDeleteFieldModal(false);
                    setFieldToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteField}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Field
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Counter Confirmation Modal */}
      {showResetCounterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-orange-600" />
                Reset Appointment Counter
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to reset the appointment counter back to the starting number?
              </p>
              <div className="bg-orange-50 rounded-lg p-3 mb-4 border border-orange-200">
                <div className="text-sm">
                  <div className="font-medium text-gray-900 mb-1">
                    Counter will reset to: <span className="text-orange-700">{appointmentNumberPrefix || 'APPT'}{appointmentNumberStart || 1000}</span>
                  </div>
                  <div className="text-gray-600 text-xs">
                    Next appointment will have number: {appointmentNumberPrefix || 'APPT'}{appointmentNumberStart || 1000}
                  </div>
                </div>
              </div>
              <p className="text-xs text-red-600 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Warning: This action cannot be undone. Existing appointments will keep their current numbers.</span>
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowResetCounterModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetCounter}
                className="px-4 py-2 text-white bg-orange-600 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Reset Counter
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ManageAppointmentCalendar;
